import React, { useState, useRef, useEffect } from "react";
import {
  StyledContactsFilter,
  FilterCount,
  FilterRow,
  SearchInput,
  SearchInputContainer,
  FilterButton,
  FilterButtonContainer,
} from "./index.styled";
import ExportButton from "./ExportButton";
import { useDebounce } from "../../../hooks/useDebounce";
import filterIcon from "../../../assets/icons/filter-icon.png";
import { Search } from "lucide-react";

export type FilterType =
  | "all"
  | "today"
  | "this_week"
  | "month"
  | "source"
  | "dateRange";

export interface FilterState {
  dateFilter?: FilterType;
  sourceKey?: string;
  startDate?: string;
  endDate?: string;
  selectedMonth?: number;
  searchQuery?: string;
}

interface ContactsFilterProps {
  onFilterChange: (filterState: FilterState) => void;
  onOpenFilterModal: () => void;
  sources?: Array<{
    key: string;
    label: string;
    color: string;
    triggerId?: number;
  }>;
  filteredCount?: number;
  totalCount?: number;
  onExport?: () => void;
  exportDisabled?: boolean;
  isExporting?: boolean;
  selectedMonth?: number;
  activeFilter?: FilterType;
  selectedSourceKey?: string;
  startDate?: string;
  endDate?: string;
  searchQuery?: string;
}

const ContactsFilter: React.FC<ContactsFilterProps> = ({
  onFilterChange,
  onOpenFilterModal,
  sources = [],
  filteredCount = 0,
  totalCount,
  onExport,
  exportDisabled = false,
  isExporting = false,
  selectedMonth,
  activeFilter: externalActiveFilter,
  selectedSourceKey: externalSelectedSourceKey,
  startDate: externalStartDate = "",
  endDate: externalEndDate = "",
  searchQuery: externalSearchQuery = "",
}) => {
  console.log("ContactsFilter received sources:", sources);
  const [currentFilterState, setCurrentFilterState] = useState<FilterState>({
    dateFilter: "all",
    sourceKey: undefined,
    startDate: "",
    endDate: "",
    selectedMonth: undefined,
    searchQuery: "",
  });

  const [searchInput, setSearchInput] = useState<string>("");

  const debouncedSearchName = useDebounce(searchInput, 500);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setCurrentFilterState({
      dateFilter: externalActiveFilter || "all",
      sourceKey: externalSelectedSourceKey,
      startDate: externalStartDate,
      endDate: externalEndDate,
      selectedMonth: selectedMonth,
      searchQuery: externalSearchQuery,
    });
    setSearchInput(externalSearchQuery);
  }, [
    externalActiveFilter,
    externalSelectedSourceKey,
    externalStartDate,
    externalEndDate,
    selectedMonth,
    externalSearchQuery,
  ]);

  useEffect(() => {
    if (debouncedSearchName !== currentFilterState.searchQuery) {
      updateFilterState({
        searchQuery: debouncedSearchName,
      });
    }
  }, [debouncedSearchName]);

  useEffect(() => {
    if (searchInputRef.current && searchInput.trim() !== "") {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  }, [debouncedSearchName, searchInput]);

  const updateFilterState = (updates: Partial<FilterState>) => {
    const newState = { ...currentFilterState, ...updates };
    setCurrentFilterState(newState);
    onFilterChange(newState);
  };

  const handleSearchNameChange = (value: string) => {
    setSearchInput(value);
  };

  return (
    <StyledContactsFilter>
      <div className="filter-container">
        <FilterRow>
          <SearchInputContainer>
            <Search size={16} color="#7e8aad" />
            <SearchInput
              ref={searchInputRef}
              type="text"
              placeholder="חיפוש"
              value={searchInput}
              onChange={(e) => handleSearchNameChange(e.target.value)}
            />
          </SearchInputContainer>

          <FilterButtonContainer>
            <FilterButton onClick={onOpenFilterModal}>
              סנן
              <img src={filterIcon} alt="filter" />
            </FilterButton>
          </FilterButtonContainer>

          {onExport && (
            <ExportButton
              onExport={onExport}
              disabled={exportDisabled}
              isExporting={isExporting}
            />
          )}
        </FilterRow>

        <FilterCount>
          סה"כ לסינון זה {filteredCount.toLocaleString()} אנשי קשר
          {filteredCount > 0 && totalCount && totalCount !== filteredCount && (
            <span style={{ color: "#666", fontSize: "14px" }}>
              {" "}
              (מתוך {totalCount.toLocaleString()})
            </span>
          )}
        </FilterCount>
      </div>
    </StyledContactsFilter>
  );
};

export default ContactsFilter;
